<template>
    <div v-if="!isListLoading" class="table-wrapper inner" ref="tableWrapper">
        <md-table class="context-menu-support custom-paginated-table" id="bottom-marker">
            <md-table-row ref="headerRow" class="table-header-row">
                <md-table-head class="fixed-col-1" :ref="'headerCol1'">
                    <md-checkbox class="table-checkbox table-head-checkbox" v-model="isSelectAll"></md-checkbox>
                </md-table-head>
                <TableHeaderDropdown
                    class="fixed-col-2"
                    :ref="'headerCol2'"
                    column-icon="keyboard_arrow_down"
                    :default-text="selectedColumn.name"
                    :dropdown-options="columnDropdownOptions"
                    :selected-option="selectedColumn"
                    @selectedOption="handleSelectedColumn"
                />
                <md-table-head class="fixed-col-3" :ref="'headerCol3'">Status</md-table-head>
                <md-table-head class="scroll-col-4">Order Date</md-table-head>
                <md-table-head v-if="showCarrierOrders" v-feature="[featureNames.CarrierMarketplace]">
                    Carrier
                </md-table-head>
                <md-table-head>Ready Delivery Date</md-table-head>
                <md-table-head>Expected Delivery Date</md-table-head>
                <md-table-head>Items</md-table-head>
                <md-table-head>Shipping Address</md-table-head>
                <md-table-head>Customer Name</md-table-head>
                <md-table-head>Shipping Contact</md-table-head>
                <md-table-head v-if="$root.isDesktop && !isReadOnlyUser">Actions</md-table-head>
            </md-table-row>
            <md-table-row v-for="(order, rowIndex) in orderList" :key="order.orderId">
                <md-table-cell class="fixed-col-1" :ref="`bodyCell${rowIndex}-1`">
                    <md-checkbox
                        class="table-checkbox"
                        :value="order"
                        v-model="selectedOrders"
                        @change="singleCheckbox(order)"
                    ></md-checkbox>
                </md-table-cell>
                <md-table-cell class="fixed-col-2" :ref="`bodyCell${rowIndex}-2`">
                    <router-link
                        v-if="selectedColumn.key == 'order-number'"
                        class="ref-link"
                        :to="{ name: 'Order Details', params: { orderId: order.orderId } }"
                        target="_blank"
                    >
                        {{ order.orderNumber }}
                    </router-link>
                    <router-link
                        v-else
                        class="ref-link"
                        :to="{ name: 'Order Details', params: { orderId: order.orderId } }"
                        target="_blank"
                    >
                        {{ order.sourceReference != null ? order.sourceReference : order.orderNumber }}
                    </router-link>
                </md-table-cell>
                <md-table-cell class="fixed-col-3" :ref="`bodyCell${rowIndex}-3`">
                    <span class="custom-badge" :class="`status-${$_getBadgeStatusColor(order.status)}`">
                        {{ order.status }}
                    </span>
                </md-table-cell>
                <md-table-cell class="scroll-col-4" :ref="`bodyCell${rowIndex}-4`">
                    {{ order.orderDate | dateFormat(DATE_TYPES.standardDate) }}
                </md-table-cell>
                <md-table-cell
                    v-if="showCarrierOrders"
                    v-feature="[featureNames.CarrierMarketplace]"
                    class="carrier-dropdown-cell"
                    :class="isOrderLoading(order.orderId) ? 'no-padding' : ''"
                    :ref="`bodyCell${rowIndex}-10`"
                >
                    <skeleton v-if="isOrderLoading(order.orderId)" height="40px"></skeleton>
                    <carrier-service-rate-dropdown
                        v-else
                        :carrier-service-rates="order.carrierServiceRates"
                        :assigned-carrier-service-rate="order.assignedCarrier"
                        :editable="canAssignCarrier"
                        @assignedCarrierChanged="
                            (eventData) =>
                                handleAssignedCarrierChanged({
                                    eventData,
                                    orderId: order.orderId
                                })
                        "
                    ></carrier-service-rate-dropdown>
                </md-table-cell>
                <md-table-cell :ref="`bodyCell${rowIndex}-5`">
                    {{ order.readyDeliveryAvailableDate | dateFormat(DATE_TYPES.standardDate) }}
                </md-table-cell>
                <md-table-cell :ref="`bodyCell${rowIndex}-6`">
                    {{ order.expectedDeliveryDate | dateFormat(DATE_TYPES.standardDate) }}
                </md-table-cell>
                <md-table-cell>
                    {{ order.itemCount }}
                </md-table-cell>
                <md-table-cell :ref="`bodyCell${rowIndex}-7`">
                    {{ order.shippingAddress != null ? order.shippingAddress.city : '' }}
                </md-table-cell>
                <md-table-cell :ref="`bodyCell${rowIndex}-8`">
                    {{ order.customerId != null ? order.customer.name : '' }}
                </md-table-cell>
                <md-table-cell :ref="`bodyCell${rowIndex}-9`">
                    {{ order.contact.name }}
                </md-table-cell>

                <md-table-cell v-if="$root.isDesktop && !isReadOnlyUser" class="action-buttons">
                    <template v-for="(action, idx) in tableBodyActionsToDisplay">
                        <md-button
                            v-if="action === 'edit'"
                            title="Update order"
                            :class="['md-warning md-just-icon md-round', isDisabled ? 'button-disabled' : '']"
                            @click="updateOrder(order)"
                            :key="idx"
                        >
                            <md-icon>edit</md-icon>
                        </md-button>
                        <md-button
                            v-if="action === 'putOnHold'"
                            :title="order.status === 'On Hold' ? 'Unhold order' : 'Place order on hold'"
                            :class="['md-info md-just-icon md-round', isDisabled ? 'button-disabled' : '']"
                            @click="putOrderOnHold(order)"
                            :key="idx"
                        >
                            <md-icon>
                                {{ order.status == 'On Hold' ? 'play_circle_outline' : 'pause' }}
                            </md-icon>
                        </md-button>
                        <md-button
                            v-if="action === 'cancel'"
                            title="Cancel order"
                            :class="['md-danger md-just-icon md-round', isDisabled ? 'button-disabled' : '']"
                            @click="cancelOrder(order)"
                            :key="idx"
                        >
                            <md-icon>close</md-icon>
                        </md-button>
                        <delete-order-button
                            v-if="!isReadOnlyUser && action === 'delete'"
                            :key="idx"
                            :order-id="order.orderId"
                            :status="order.status"
                            :can-delete="order.canDelete"
                            @orderDeleted="handleDelete"
                        ></delete-order-button>
                    </template>
                </md-table-cell>
            </md-table-row>
        </md-table>
        <div v-if="orderList.length == 0">
            <p class="no-result-message">No results matching your search/filter could be found.</p>
        </div>
    </div>
    <div v-else>
        <div class="loader">
            <fade-loader :loading="true" color="#333333" />
            <span>LOADING</span>
        </div>
    </div>
</template>

<script>
import FadeLoader from 'vue-spinner/src/FadeLoader';
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { TableHeaderDropdown } from '@/components';
import { GeneralMixin, OrderMixin } from '@/mixins';
import { DATE_TYPES, FEATURE_NAMES } from '@/utils/constants';
import { FeatureManager } from '@/directives';
import Skeleton from './Skeleton';
import CarrierServiceRateDropdown from './CarrierServiceRateDropdown';
import DeleteOrderButton from '../Buttons/DeleteOrderButton';

export default {
    name: 'OrdersTable',
    mixins: [GeneralMixin, FeatureManager, OrderMixin],
    components: {
        FadeLoader,
        TableHeaderDropdown,
        Skeleton,
        CarrierServiceRateDropdown,
        DeleteOrderButton
    },
    props: {
        orderList: {
            type: Array,
            required: true
        },
        isListLoading: {
            type: Boolean,
            default: false
        },
        showCarrierOrders: {
            type: Boolean,
            default: false
        },
        selectedOrders: {
            type: Array,
            required: true
        },
        columnDropdownOptions: {
            type: Array,
            required: true
        },
        selectedColumn: {
            type: Object,
            required: true
        },
        isDisabled: {
            type: Boolean,
            default: false
        },
        tableBodyActionsToDisplay: {
            type: Array,
            required: true
        },
        canAssignCarrier: {
            type: Boolean,
            default: false
        },
        loadingOrders: {
            type: Array,
            default: () => []
        },
        orderRatesFetchStatus: {
            type: String,
            default: 'idle'
        }
    },
    data() {
        return {
            featureNames: FEATURE_NAMES,
            DATE_TYPES,
            debouncedHandleResize: null,
            columnWidths: [46, 100, 100],
            scrollHeight: 0
        };
    },
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser'
        }),
        isSelectAll: {
            get() {
                return this.orderList.length > 0 && this.selectedOrders.length === this.orderList.length;
            },
            set(value) {
                this.$emit('select-all-changed', value);
            }
        },
        hasCarrierColumn() {
            return this.showCarrierOrders && this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace);
        }
    },
    mounted() {
        this.debouncedHandleResize = _.debounce(this.calculateColumnWidths, 500);
        window.addEventListener('resize', this.debouncedHandleResize);
        this.calculateColumnWidths();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.debouncedHandleResize);
    },
    watch: {
        orderList: {
            handler() {
                this.$nextTick(() => {
                    this.calculateColumnWidths();
                });
            },
            deep: true
        }
    },
    methods: {
        isOrderLoading(orderId) {
            return this.loadingOrders.includes(orderId) || this.orderRatesFetchStatus === 'pending';
        },
        singleCheckbox(order) {
            this.$emit('single-checkbox-changed', order);
        },
        handleSelectedColumn(item) {
            this.$emit('column-selected', item);
        },
        handleAssignedCarrierChanged(data) {
            this.$emit('assigned-carrier-changed', data);
        },
        updateOrder(order) {
            this.$emit('update-order', order);
        },
        handleDelete(order) {
            this.$emit('delete-order', order);
        },
        putOrderOnHold(order) {
            this.$emit('put-order-on-hold', order);
        },
        cancelOrder(order) {
            this.$emit('cancel-order', order);
        },
        handleDownloadLabelButtonClicked(orderId) {
            this.$emit('download-label-clicked', orderId);
        },
        calculateColumnWidths() {
            this.$nextTick(() => {
                // Calculate widths for first 3 columns
                const newWidths = [46, 100, 100];
                let cellHeight = 0;

                // Calculate the maximum height for each column based on content
                for (let row = 0; row < this.orderList.length; row++) {
                    let maxColumn = 11;
                    if (this.hasCarrierColumn) {
                        maxColumn = 12; // If Carrier Marketplace feature is enabled, we have an extra column
                    }
                    for (let col = 0; col < maxColumn; col++) {
                        const cell = this.$refs[`bodyCell${row}-${col + 1}`];
                        if (cell) {
                            const cellEl = cell[0].$el;

                            if (cellEl) {
                                cellHeight = Math.max(cellEl.offsetHeight, cellHeight);
                            }
                        }
                    }
                }

                // Calculate the maximum width for each column based on content, we only check the first 3 columns
                for (let row = 0; row < this.orderList.length; row++) {
                    for (let col = 0; col < 3; col++) {
                        const cell = this.$refs[`bodyCell${row}-${col + 1}`];
                        if (cell) {
                            const cellEl = cell[0].$el;
                            if (cellEl) {
                                let finalWidth = cellEl.scrollWidth;
                                if (col > 0 && finalWidth < 100) {
                                    finalWidth = 100; // Ensure minimum width of 100px
                                }

                                if (col === 1 && finalWidth > 105) {
                                    // Keep consistent width for Order#/Source Ref column
                                    finalWidth = 140;
                                }

                                if (col === 2 && finalWidth > 120) {
                                    finalWidth = 160; // the longest length of the status will fit this width
                                }
                                newWidths[col] = Math.max(newWidths[col], finalWidth);
                            }
                        }
                    }
                }

                this.columnWidths = newWidths;
                this.scrollHeight = cellHeight;

                // Update CSS custom properties
                document.documentElement.style.setProperty('--col1-width', `${this.columnWidths[0]}px`);
                document.documentElement.style.setProperty('--col2-width', `${this.columnWidths[1]}px`);
                document.documentElement.style.setProperty('--col3-width', `${this.columnWidths[2]}px`);
                document.documentElement.style.setProperty('--table-cell-height', `${this.scrollHeight}px`);
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.loader {
    position: absolute;
    top: 40%;
    left: 50%;

    span {
        position: absolute;
        margin-top: 50px;
        width: 110px;
        left: calc((100% - 100px) / 2);
        text-align: center;
        font-weight: 600;
    }
}

.ref-link {
    color: rgba(0, 0, 0, 0.87) !important;
}

.ref-link:hover {
    color: #2b93ff !important;
}

.custom-badge {
    background-color: #c2907f;
    max-width: initial !important;
}

.custom-paginated-table {
    overflow-y: auto;
}

.table-wrapper {
    margin-left: calc(var(--col1-width) + var(--col2-width) + var(--col3-width) - 20px);
}



/* Fixed columns styling */
.md-table-head.fixed-col-1,
.md-table-cell.fixed-col-1 {
    position: absolute;
    left: 10px;
    z-index: 2;
    margin-top: -0.5px;
    height: var(--table-cell-height);
}

.md-table-head.fixed-col-2,
.md-table-cell.fixed-col-2 {
    position: absolute;
    width: var(--col2-width);
    left: var(--col1-width);
    z-index: 2;
    margin-top: -0.5px;
    height: var(--table-cell-height);
}

.md-table-cell.fixed-col-2 {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    max-width: 200px;
}

.md-table-head.fixed-col-3,
.md-table-cell.fixed-col-3 {
    position: absolute;
    width: var(--col3-width);
    left: calc(var(--col1-width) + var(--col2-width));
    z-index: 2;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
    margin-top: -0.5px;
    height: var(--table-cell-height);
}

.md-table-cell.fixed-col-3 {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

/* Carrier column styling - scrollable column */
.md-table-cell.carrier-dropdown-cell {
    padding: 8px 12px;
    min-width: 280px; /* Ensure adequate width for dropdown */
}

.scroll-col-4 {
    padding-left: 30px;
}

// /* Header specific styling */
.md-table-head.fixed-col-1,
.md-table-head.fixed-col-2 {
    z-index: 3; /* Higher than body cells */
}

.md-table-row:hover {
    background-color: rgba(0, 0, 0, 0.04);
    .fixed-col-1,
    .fixed-col-2,
    .fixed-col-3 {
        background-color: rgba(0, 0, 0, 0.04) !important;
    }
}

.table-header-row:hover {
    background-color: transparent;
    .fixed-col-1,
    .fixed-col-2,
    .fixed-col-3 {
        background-color: transparent !important;
    }
}

/* Ensure consistent checkbox positioning */
.md-table-cell.fixed-col-1 {
    display: flex;
    align-items: center;
    justify-content: center;
}

.table-checkbox {
    margin: 0 !important;
}

/* Prevent text overflow in Order#/Source Ref column */
//.md-table-cell.fixed-col-2 .ref-link {
//    display: block;
//    overflow: hidden;
//    text-overflow: ellipsis;
//    white-space: nowrap;
//    max-width: 100%;
//}

.action-buttons {
    ::v-deep .md-table-cell-container {
        width: 130px;
    }
}
</style>
